/**
 * useAIStudio Hook
 *
 * Main React hook for AI Studio state management.
 * Manages generation modes, sessions, preferences, and image generation workflow.
 *
 * React 19 patterns:
 * - useState for simple state values
 * - useCallback for event handlers
 * - useMemo for derived values
 * - useEffect for preference initialization
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  BatchSize,
  IUseAIStudioReturn,
  IAIStudioSession,
  IGeneratedParams,
  IAIStudioPrompt,
  SceneCategory,
} from '@/@types/ai-studio';

// ============================================================================
// Types
// ============================================================================

/**
 * Result type from generateAIDriven action
 */
interface IGenerateAIDrivenResult {
  success: boolean;
  sessionId?: Id<'aiStudioSessions'>;
  finalUrl?: string;
  params?: {
    sceneId: string;
    sceneName: string;
    sceneCategory: SceneCategory;
    sceneDescription: string;
    pose: string;
    timeOfDay: string;
    outfitStyle: string;
    mood: string;
    shotType: string;
    colorPalette: string;
    weather?: string;
  };
  error?: string;
}

/**
 * Session data returned from Convex query (matches backend schema)
 */
interface IConvexSession {
  _id: Id<'aiStudioSessions'>;
  _creationTime: number;
  userId: Id<'users'>;
  referencePersonId?: Id<'referencePersons'>;
  uploadedReferenceIds?: Id<'_storage'>[];
  generatedScene: string;
  generatedParams: {
    scene: string;
    pose: string;
    timeOfDay: string;
    outfitStyle: string;
    mood: string;
    shotType: string;
    colorPalette: string;
    weather?: string;
  };
  previewStorageId?: Id<'_storage'>;
  previewUrl?: string | null;
  finalStorageId?: Id<'_storage'>;
  finalUrl?: string | null;
  status: 'generated' | 'discarded' | 'preview' | 'selected' | 'upscaled';
  prompt: string;
  isDeleted: boolean;
  createdAt: number;
}

/**
 * Transform Convex session to IAIStudioSession format
 */
function transformSession(session: IConvexSession): IAIStudioSession {
  return {
    _id: session._id,
    _creationTime: session._creationTime,
    userId: session.userId,
    referencePersonId: session.referencePersonId,
    params: {
      sceneId: session.generatedParams.scene,
      sceneName: session.generatedScene,
      sceneCategory: 'lifestyle', // Default category (not stored in DB)
      sceneDescription: session.generatedScene,
      pose: session.generatedParams.pose,
      timeOfDay: session.generatedParams.timeOfDay,
      outfitStyle: session.generatedParams.outfitStyle,
      mood: session.generatedParams.mood,
      shotType: session.generatedParams.shotType,
      colorPalette: session.generatedParams.colorPalette,
      weather: session.generatedParams.weather,
    },
    imageStorageId: session.finalStorageId ?? session.previewStorageId,
    imageUrl: session.finalUrl ?? session.previewUrl ?? undefined,
    status: session.status,
    prompt: session.prompt,
    createdAt: session.createdAt,
    updatedAt: session.createdAt, // Using createdAt as placeholder
  };
}

// ============================================================================
// Hook
// ============================================================================

export function useAIStudio(): IUseAIStudioReturn {
  // ============================================================================
  // Local State
  // ============================================================================

  const [batchSize, setBatchSizeState] = useState<BatchSize>(2);
  const [selectedReferencePersonId, setSelectedReferencePersonId] = useState<
    Id<'referencePersons'> | undefined
  >(undefined);
  const [generatedParams, setGeneratedParams] = useState<IGeneratedParams | null>(null);
  // Note: setCurrentPrompt will be used when prompt preview feature is implemented
  const [currentPrompt, _setCurrentPrompt] = useState<IAIStudioPrompt | null>(null);
  // Selected session IDs for multi-selection (checkbox toggles)
  const [selectedSessionIds, setSelectedSessionIds] = useState<Id<'aiStudioSessions'>[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingParams, setIsGeneratingParams] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preferencesInitialized, setPreferencesInitialized] = useState(false);

  // ============================================================================
  // Convex Queries
  // ============================================================================

  const sessionsData = useQuery(api.aiStudio.sessions.getRecentSessions, { limit: 20 });
  const preferencesData = useQuery(api.aiStudio.preferences.getPreferences, {});

  // ============================================================================
  // Convex Mutations
  // ============================================================================

  const deleteSessionMutation = useMutation(api.aiStudio.sessions.deleteSession);
  const bulkDeleteSessionsMutation = useMutation(api.aiStudio.sessions.bulkDeleteSessions);

  // ============================================================================
  // Convex Actions
  // ============================================================================

  const generateAIDrivenAction = useAction(api.aiStudio.generation.generateAIDriven);

  // ============================================================================
  // Derived State
  // ============================================================================

  const isLoadingSessions = sessionsData === undefined;

  const sessions: IAIStudioSession[] = useMemo(() => {
    if (!sessionsData) return [];
    return sessionsData.map((session: IConvexSession) => transformSession(session));
  }, [sessionsData]);


  // ============================================================================
  // Initialize from Preferences
  // ============================================================================

  useEffect(() => {
    if (preferencesData && !preferencesInitialized) {
      setBatchSizeState(preferencesData.defaultBatchSize as BatchSize);
      setPreferencesInitialized(true);
    }
  }, [preferencesData, preferencesInitialized]);

  // ============================================================================
  // Settings Handlers
  // ============================================================================

  const setBatchSize = useCallback((newSize: BatchSize) => {
    setBatchSizeState(newSize);
  }, []);

  const selectReferencePerson = useCallback(
    (personId: Id<'referencePersons'> | undefined) => {
      setSelectedReferencePersonId(personId);
    },
    []
  );

  // ============================================================================
  // Parameter Generation (Preview-First Mode)
  // ============================================================================

  const generateParams = useCallback(async (): Promise<void> => {
    // For preview-first mode, params are generated as part of generatePreviews
    // This is a placeholder for future standalone param generation
    setIsGeneratingParams(true);
    setError(null);

    try {
      // Parameters will be generated during preview generation
      // This function can be extended to support standalone param generation
      setIsGeneratingParams(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate parameters';
      setError(errorMessage);
      setIsGeneratingParams(false);
    }
  }, []);

  const updateParams = useCallback((params: Partial<IGeneratedParams>) => {
    setGeneratedParams((prev) => {
      if (!prev) return null;
      return { ...prev, ...params };
    });
  }, []);

  // ============================================================================
  // Image Generation
  // ============================================================================

  const generate = useCallback(async (): Promise<void> => {
    if (!selectedReferencePersonId) {
      setError('Please select a reference person');
      toast.error('No reference person selected', {
        description: 'Please select a reference person before generating.',
      });
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Generate batch of 2K images in parallel
      const generatePromises = Array.from({ length: batchSize }, () =>
        generateAIDrivenAction({
          referencePersonId: selectedReferencePersonId,
        })
      );

      const results: IGenerateAIDrivenResult[] = await Promise.all(generatePromises);
      const successfulResults = results.filter((r: IGenerateAIDrivenResult) => r.success);
      const failedCount = results.length - successfulResults.length;

      if (successfulResults.length === 0) {
        const firstError = results.find((r: IGenerateAIDrivenResult) => r.error)?.error;
        setError(firstError ?? 'Generation failed');
        toast.error('Image generation failed', {
          description: firstError ?? 'Please try again.',
        });
        return;
      }

      // Set params from first successful result
      const firstSuccess = successfulResults[0];
      if (firstSuccess.params) {
        setGeneratedParams(firstSuccess.params);
      }

      if (failedCount > 0) {
        toast.success(`${successfulResults.length} image(s) generated!`, {
          description: `${failedCount} generation(s) failed.`,
        });
      } else {
        toast.success(`${successfulResults.length} image(s) generated!`, {
          description: 'Your images are ready.',
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      toast.error('Generation failed', {
        description: errorMessage,
      });
    } finally {
      setIsGenerating(false);
    }
  }, [batchSize, selectedReferencePersonId, generateAIDrivenAction]);

  // ============================================================================
  // Session Management
  // ============================================================================

  const selectSession = useCallback((sessionId: Id<'aiStudioSessions'>) => {
    setSelectedSessionIds((currentIds) =>
      currentIds.includes(sessionId)
        ? currentIds.filter((id) => id !== sessionId)
        : [...currentIds, sessionId]
    );
  }, []);

  const clearSelectedSessions = useCallback(() => {
    setSelectedSessionIds([]);
  }, []);

  const selectAllSessions = useCallback(() => {
    setSelectedSessionIds(sessions.map((s) => s._id));
  }, [sessions]);

  const discardSession = useCallback(
    async (sessionId: Id<'aiStudioSessions'>): Promise<void> => {
      try {
        await deleteSessionMutation({
          sessionId,
        });
        // Remove from selection if selected
        setSelectedSessionIds((ids) => ids.filter((id) => id !== sessionId));
        toast.success('Image deleted');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete image';
        setError(errorMessage);
        toast.error('Failed to delete image', {
          description: errorMessage,
        });
      }
    },
    [deleteSessionMutation]
  );

  const bulkDeleteSessions = useCallback(
    async (sessionIds: Id<'aiStudioSessions'>[]): Promise<void> => {
      if (sessionIds.length === 0) {
        toast.error('No images selected', {
          description: 'Please select images to delete.',
        });
        return;
      }

      try {
        const result = await bulkDeleteSessionsMutation({ sessionIds });

        // Clear selection after deletion
        setSelectedSessionIds([]);

        if (result.deletedCount > 0) {
          toast.success(`${result.deletedCount} image(s) deleted`, {
            description:
              result.failedCount > 0
                ? `${result.failedCount} image(s) could not be deleted.`
                : undefined,
          });
        } else {
          toast.error('Failed to delete images', {
            description: 'No images were deleted.',
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete images';
        setError(errorMessage);
        toast.error('Failed to delete images', {
          description: errorMessage,
        });
      }
    },
    [bulkDeleteSessionsMutation]
  );

  const downloadSession = useCallback(
    async (sessionId: Id<'aiStudioSessions'>): Promise<void> => {
      const session = sessions.find((s) => s._id === sessionId);
      if (!session?.imageUrl) {
        toast.error('No image available', {
          description: 'This session does not have an image to download.',
        });
        return;
      }

      try {
        // Fetch the image and trigger download
        const response = await fetch(session.imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ai-studio-${sessionId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Image downloaded');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Download failed';
        toast.error('Download failed', {
          description: errorMessage,
        });
      }
    },
    [sessions]
  );

  const clearSessions = useCallback(async (): Promise<void> => {
    try {
      // Soft delete all sessions by marking them as discarded
      const deletePromises = sessions.map((session) =>
        deleteSessionMutation({ sessionId: session._id })
      );
      await Promise.all(deletePromises);
      toast.success('All sessions cleared');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear sessions';
      setError(errorMessage);
      toast.error('Failed to clear sessions', {
        description: errorMessage,
      });
    }
  }, [sessions, deleteSessionMutation]);

  // ============================================================================
  // Error Handling
  // ============================================================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    // State
    batchSize,
    sessions,
    selectedReferencePersonId,
    generatedParams,
    currentPrompt,
    selectedSessionIds,

    // Loading states
    isGenerating,
    isLoadingSessions,
    isGeneratingParams,

    // Error state
    error,

    // Actions
    setBatchSize,
    selectReferencePerson,
    generateParams,
    updateParams,
    generate,
    selectSession,
    clearSelectedSessions,
    selectAllSessions,
    discardSession,
    bulkDeleteSessions,
    downloadSession,
    clearSessions,
    clearError,
  };
}
